import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' }
});

// Auth
export const authMe = () => api.get('/auth/me');
export const authLogout = () => api.post('/auth/logout');
export const authSignup = (email, password) => api.post('/auth/signup', { email, password });
export const authVerifyOtp = (email, otp) => api.post('/auth/verify-otp', { email, otp });
export const authResendOtp = (email) => api.post('/auth/resend-otp', { email });
export const authLogin = (email, password) => api.post('/auth/login', { email, password });
export const authGoogleUrl = (mode) => api.get('/auth/google', { params: { mode } });
export const authGoogleExchange = (access_token, mode) => api.post('/auth/google/exchange', { access_token, mode });
export const authForgotPassword = (email) => api.post('/auth/forgot-password', { email });
export const authResetPassword = (email, otp, new_password) => api.post('/auth/reset-password', { email, otp, new_password });

// Profile
export const getProfile = () => api.get('/profile');
export const updateProfile = (data) => api.put('/profile', data);
export const uploadLogo = (file) => {
  const fd = new FormData();
  fd.append('file', file);
  return api.post('/profile/logo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const uploadProfilePicture = (file) => {
  const fd = new FormData();
  fd.append('file', file);
  return api.post('/profile/picture', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
};

// Dashboard
export const getDashboard = () => api.get('/dashboard');

// Clients
export const getClients = (search) => api.get('/clients', { params: { search } });
export const createClient = (data) => api.post('/clients', data);
export const getClient = (id) => api.get(`/clients/${id}`);
export const updateClient = (id, data) => api.put(`/clients/${id}`, data);
export const deleteClient = (id) => api.delete(`/clients/${id}`);

// Proposals
export const getProposals = (status) => api.get('/proposals', { params: { status } });
export const createProposal = (data) => api.post('/proposals', data);
export const getProposal = (id) => api.get(`/proposals/${id}`);
export const updateProposal = (id, data) => api.put(`/proposals/${id}`, data);
export const sendProposal = (id) => api.post(`/proposals/${id}/send`);
export const generateAIProposal = (data) => api.post('/proposals/generate-ai', data);

// Public Proposals
export const getPublicProposal = (token) => api.get(`/public/proposals/${token}`);
export const acceptProposal = (token, data) => api.post(`/public/proposals/${token}/accept`, data);
export const rejectProposal = (token, data) => api.post(`/public/proposals/${token}/reject`, data);

// Projects
export const getProjects = () => api.get('/projects');
export const createProject = (data) => api.post('/projects', data);
export const getProject = (id) => api.get(`/projects/${id}`);
export const updateProject = (id, data) => api.put(`/projects/${id}`, data);

// Deliverables
// invoiceId is optional — pass it to gate the new file to that invoice in the
// same request instead of uploading then calling assignDeliverableInvoice.
export const uploadDeliverable = (projectId, file, invoiceId) => {
  const fd = new FormData();
  fd.append('file', file);
  if (invoiceId) fd.append('invoice_id', invoiceId);
  return api.post(`/projects/${projectId}/deliverables`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const getDeliverables = (projectId) => api.get(`/projects/${projectId}/deliverables`);
// invoiceId null => file unlocks on any paid invoice for the project.
export const assignDeliverableInvoice = (projectId, deliverableId, invoiceId) =>
  api.patch(`/projects/${projectId}/deliverables/${deliverableId}`, { invoice_id: invoiceId });
export const deleteDeliverable = (projectId, deliverableId) => api.delete(`/projects/${projectId}/deliverables/${deliverableId}`);
export const getProjectDownloads = (projectId) => api.get(`/projects/${projectId}/downloads`);

// Invoices
export const getInvoices = (status) => api.get('/invoices', { params: { status } });
export const createInvoice = (data) => api.post('/invoices', data);
export const getInvoice = (id) => api.get(`/invoices/${id}`);
export const updateInvoice = (id, data) => api.put(`/invoices/${id}`, data);
export const sendInvoice = (id) => api.post(`/invoices/${id}/send`);
export const deleteInvoice = (id) => api.delete(`/invoices/${id}`);

// Public Invoices
export const getPublicInvoice = (token) => api.get(`/public/invoices/${token}`);
export const createCheckoutSession = (token) => api.post(`/public/invoices/${token}/checkout`);
export const verifyPayment = (data) => api.post('/payments/verify', data);

// Public Delivery
export const getPublicDelivery = (token) => api.get(`/public/delivery/${token}`);

// Templates
export const getTemplates = () => api.get('/templates');
export const createTemplate = (data) => api.post('/templates', data);
export const getTemplate = (id) => api.get(`/templates/${id}`);
export const updateTemplate = (id, data) => api.put(`/templates/${id}`, data);
export const deleteTemplate = (id) => api.delete(`/templates/${id}`);

// Time Entries
export const getTimeEntries = (projectId) => api.get(`/projects/${projectId}/time-entries`);
export const getUninvoicedTimeEntries = (projectId) => api.get(`/projects/${projectId}/time-entries/uninvoiced`);
export const createTimeEntry = (projectId, data) => api.post(`/projects/${projectId}/time-entries`, data);
export const deleteTimeEntry = (projectId, entryId) => api.delete(`/projects/${projectId}/time-entries/${entryId}`);

// Analytics
export const getAnalytics = () => api.get('/analytics');

// Client Portal
export const generatePortalToken = (clientId) => api.post(`/clients/${clientId}/portal-token`);
export const getClientPortal = (token) => api.get(`/public/portal/${token}`);

// Subscriptions
export const createSubscription = (plan) => api.post('/subscriptions/create', { plan });
export const verifySubscription = (data) => api.post('/subscriptions/verify', data);
export const cancelSubscription = () => api.post('/subscriptions/cancel');
export const getSubscriptionStatus = () => api.get('/subscriptions/status');

// File download
export const downloadFile = (path) => api.get(`/files/${path}`, { responseType: 'blob' });

export { API, BACKEND_URL };
export default api;
